// App.js
import { Suspense, lazy, useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Home from './components/Home';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import BasicAppBar from './components/BasicAppBar';
import CookieNotice from './components/CookieNotice';
import Box from '@mui/material/Box';
import { auth } from './firebase';
import PrivateRoute from './components/PrivateRoute';

import { pinkColor } from './utils/constants';
import { setAnalyticsIdentity } from './utils/analytics';

// Back-office and secondary screens load only when opened, so the homepage stays light.
const Dashboard = lazy(() => import('./components/Dashboard'));
const ProfilePage = lazy(() => import('./components/ProfilePage'));
const StaffManagement = lazy(() => import('./components/StaffManagement'));
const GoldMembershipTerms = lazy(() => import('./components/GoldMembershipTerms'));

const theme = createTheme({
  palette: {
    primary: {
      main: '#000000',
    },
    secondary: {
      main: pinkColor,
    },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setLoading(true);
      if (user) {
        setIsAuthenticated(true);
        setUser(user);
        try {
          const idTokenResult = await user.getIdTokenResult();
          setRole(idTokenResult.claims.role || null);
          setAnalyticsIdentity(user, idTokenResult.claims.role || null);
        } catch (error) {
          console.error('Error fetching custom claims:', error);
          setRole(null);
          setAnalyticsIdentity(user, null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setRole(null);
        setAnalyticsIdentity(null, null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <Box sx={{ overflow: 'auto', height: '100vh', backgroundColor: '#000000' }}>
          <BasicAppBar title={'Olivia Salon & Academy'} bgColor={'primary'} />
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/gold-membership" element={<GoldMembershipTerms />} />
            <Route
              path="/dashboard/*"
              element={
                loading ? null : <PrivateRoute isAuthenticated={isAuthenticated} allowedRoles={['admin', 'manager', 'staff']} userRole={role}>
                  <Dashboard role={role} />
                </PrivateRoute>
              }
            />
            <Route
              path="/staff"
              element={
                loading ? null : <PrivateRoute isAuthenticated={isAuthenticated} allowedRoles={['admin', 'manager']} userRole={role}>
                  <StaffManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                loading ? null : <PrivateRoute isAuthenticated={isAuthenticated} allowedRoles={['admin', 'manager', 'staff']} userRole={role}>
                  <ProfilePage user={user} />
                </PrivateRoute>
              }
            />
          </Routes>
          </Suspense>
        </Box>
        <CookieNotice />
      </Router>
    </ThemeProvider>
  );
}

export default App;
